import { z } from "zod";

import packageJSON from "../package.json";

const stringSchema = z.string({ required_error: "Not defined" });
const urlSchema = stringSchema.url().refine((val) => val.at(-1) === "/", {
    message: "url should end with the character '/'",
});
const booleanFlagSchema = z
    .string()
    .refine((val) => val === "" || val === undefined || val === "true" || val === "false", {
        message: "Must be 'true', 'false' or undefined",
    })
    .transform((val) => val === "true");

const environments = z.enum(["production", "acceptance", "test", "integration", "development"]);
export type ENVIRONMENTS = z.infer<typeof environments>;
const configSchema = z.object({
    APP_ENV: environments.optional(),
    API_URL: urlSchema.default(`${window.location.origin}/`),
    API_JS_URL: urlSchema.default(`${window.location.origin}/`),
    APP_NAME: stringSchema.default("Service Support App"),
    APP_TITLE: stringSchema.default("Lely Service Support App"),
    APP_VERSION: stringSchema.default(`v${packageJSON.version}`),
    CLIENT_ID: stringSchema,
    SENTRY_DSN: stringSchema.optional(),
    AZURE_AUTH_AUTHORITY_URL: stringSchema,
    AZURE_AUTH_CLIENT_ID: stringSchema.uuid(),
    AZURE_AUTH_REDIRECT_URL: stringSchema.url().optional(),
    AZURE_AUTH_POST_LOGOUT_REDIRECT_URL: stringSchema.url().optional(),
    AZURE_AUTH_SCOPE: stringSchema,
    AZURE_AUTH_ENABLED: booleanFlagSchema.optional(),
});

let config: z.infer<typeof configSchema>;
const result = configSchema.safeParse({
    APP_ENV: import.meta.env.VITE_APP_ENV,
    API_URL: import.meta.env.VITE_API_URL,
    API_JS_URL: import.meta.env.VITE_API_JS_URL,
    APP_NAME: import.meta.env.APP_NAME,
    APP_TITLE: import.meta.env.APP_TITLE,
    APP_VERSION: import.meta.env.APP_VERSION,
    CLIENT_ID: import.meta.env.VITE_CLIENT_ID,
    SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
    AZURE_AUTH_AUTHORITY_URL: import.meta.env.VITE_AZURE_AUTH_AUTHORITY_URL,
    AZURE_AUTH_CLIENT_ID: import.meta.env.VITE_AZURE_AUTH_CLIENT_ID,
    AZURE_AUTH_REDIRECT_URL: import.meta.env.VITE_AZURE_AUTH_REDIRECT_URL,
    AZURE_AUTH_LOGOUT_REDIRECT_URL: import.meta.env.VITE_AZURE_AUTH_POST_LOGOUT_REDIRECT_URL,
    AZURE_AUTH_SCOPE: import.meta.env.VITE_AZURE_AUTH_SCOPE,
    AZURE_AUTH_ENABLED: import.meta.env.VITE_AZURE_AUTH_ENABLED,
});

class EnvironmentVariablesError extends Error {
    override name = "EnvironmentVariablesError";

    constructor(errors: Record<string, string[]>) {
        const message = Object.entries(errors)
            .map(([key, errors]) => `${key}: ${errors.join(", ")}`)
            .join("\n");

        super(`\n\nErrors while validating environment variables:\n\n${message}\n\n`);
    }
}

if (result.success) {
    config = result.data;
} else {
    // FIXME Should work after enabling TS strict mode
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const errors = result.error.flatten();
    throw new EnvironmentVariablesError(errors.fieldErrors);
}

// eslint-disable-next-line no-console
console.log(`${config.APP_NAME} ${config.APP_VERSION} on "${config.APP_ENV}" environment`);

export { config };
